import React, { useCallback, useId, useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { BaseTable, TableColumn } from '../../common/Table';
import { StatusChip, ActionButton } from '../../common/Table/Table.styles';
import {
  catalogActionIconButtonSx,
  catalogActionSvgIconGradientSx,
} from '../../products/catalogGradients';
import { CatalogActionIconGradientDefs } from '../../products/CatalogActionIconGradientDefs';
import type { SellerManagementRow } from './types';

const formatMoney = (value: number): string =>
  new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency: 'BOB',
    maximumFractionDigits: 2,
  }).format(value);

export interface SellerManagementTableProps {
  sellers: SellerManagementRow[];
  onViewSeller: (id: string) => void;
}

const SellerManagementTable: React.FC<SellerManagementTableProps> = ({ sellers, onViewSeller }) => {
  const columns: TableColumn<SellerManagementRow>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Nombre',
        sortAccessor: (item) => `${item.firstName} ${item.lastName}`,
        render: (item) => (
          <Typography color="text.primary" fontWeight={600} fontSize="1rem">
            {item.firstName} {item.lastName}
          </Typography>
        ),
      },
      {
        id: 'email',
        label: 'Correo',
        sortAccessor: (item) => item.email,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.email}
          </Typography>
        ),
      },
      {
        id: 'phone',
        label: 'Teléfono',
        sortAccessor: (item) => item.phone,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {(item.phone?.trim() ?? '') !== '' ? item.phone!.trim() : '—'}
          </Typography>
        ),
      },
      {
        id: 'locationName',
        label: 'Sucursal',
        sortAccessor: (item) => item.locationName,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.locationName ?? '—'}
          </Typography>
        ),
      },
      {
        id: 'isActive',
        label: 'Estado',
        sortAccessor: (item) => (item.isActive ? 1 : 0),
        render: (item) => (
          <StatusChip
            label={item.isActive ? 'Activo' : 'Inactivo'}
            size="small"
            color={item.isActive ? 'success' : 'default'}
          />
        ),
      },
      {
        id: 'totalSales',
        label: 'Total ventas',
        sortAccessor: (item) => item.totalSales,
        render: (item) => (
          <Typography color="success.main" fontWeight={600} fontSize="1rem">
            {formatMoney(item.totalSales)}
          </Typography>
        ),
      },
      {
        id: 'salesCount',
        label: 'N ventas',
        sortAccessor: (item) => item.salesCount,
        render: (item) => (
          <Typography color="text.secondary" fontWeight={500} fontSize="0.95rem">
            {item.salesCount}
          </Typography>
        ),
      },
    ],
    [],
  );

  const catalogIconGradientId = `seller-mgmt-action-grad-${useId().replace(/:/g, '')}`;
  const iconGradSx = catalogActionSvgIconGradientSx(catalogIconGradientId);

  const handleView = useCallback((id: string) => onViewSeller(id), [onViewSeller]);

  return (
    <>
      <CatalogActionIconGradientDefs id={catalogIconGradientId} />
      <BaseTable<SellerManagementRow>
        items={sellers}
        columns={columns}
        showActions
        getItemId={(item) => item.id}
        emptyStateTitle="No se encontraron vendedores"
        renderActions={(row) => (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Ver detalles" arrow>
              <ActionButton
                size="small"
                onClick={() => handleView(row.id)}
                sx={catalogActionIconButtonSx}
              >
                <ViewIcon fontSize="small" sx={iconGradSx} />
              </ActionButton>
            </Tooltip>
          </Box>
        )}
      />
    </>
  );
};

export default SellerManagementTable;
